import { Box, ButtonBase, Typography } from "@mui/material";
import { useState } from "react";
import { MdChevronRight } from "react-icons/md";

import type { HomePhase } from "../types/home.types";
import { HomePanel } from "./HomePanel";
import { HOME_COLORS } from "./homeTheme";

/** Cor da barra de cada fase, na ordem do funil. */
const PHASE_COLORS: Record<string, string> = {
  comercial: "#1361A4",
  execucao: "#0F7C86",
  faturamento: "#B35A00",
  auditoria: "#6B3FA0",
};

interface PhasesPanelProps {
  phases?: HomePhase[];
  onOpenStatus(status: { key: string; label: string }): void;
}

/**
 * Projetos ativos por fase. A barra é proporcional à fase com mais projetos;
 * clicar na fase mostra os status dela, e cada status abre a lista filtrada.
 */
export function PhasesPanel({ phases, onOpenStatus }: PhasesPanelProps) {
  const [openPhase, setOpenPhase] = useState<string | null>("comercial");
  const total = phases?.reduce((sum, phase) => sum + phase.total, 0) ?? 0;
  const max = Math.max(1, ...(phases ?? []).map((phase) => phase.total));

  return (
    <HomePanel
      title="Projetos ativos por fase"
      aside={phases ? `${total} ativos. Clique para abrir.` : undefined}
      flex="1 1 320px"
    >
      <Box display="flex" flexDirection="column">
        {(phases ?? []).map((phase) => {
          const open = openPhase === phase.key;
          return (
            <Box key={phase.key}>
              <ButtonBase
                aria-expanded={open}
                onClick={() => setOpenPhase(open ? null : phase.key)}
                sx={{
                  width: "100%",
                  display: "flex",
                  flexDirection: "column",
                  alignItems: "stretch",
                  gap: 1,
                  py: 1.5,
                  px: 0.5,
                  textAlign: "left",
                  borderTop: `1px solid ${HOME_COLORS.line}`,
                  "&:hover": { bgcolor: HOME_COLORS.soft },
                }}
              >
                <Box display="flex" alignItems="center" gap={1}>
                  <Box
                    component="span"
                    display="flex"
                    sx={{
                      transition: "transform .15s",
                      transform: open ? "rotate(90deg)" : "none",
                    }}
                  >
                    <MdChevronRight size={16} />
                  </Box>
                  <Typography sx={{ flex: 1, fontSize: 14, fontWeight: 500 }}>
                    {phase.label}
                  </Typography>
                  <Typography sx={{ fontSize: 14, fontWeight: 700 }}>
                    {phase.total}
                  </Typography>
                </Box>
                <Box
                  sx={{
                    height: 8,
                    borderRadius: "4px",
                    bgcolor: HOME_COLORS.line,
                    overflow: "hidden",
                  }}
                >
                  <Box
                    sx={{
                      height: "100%",
                      borderRadius: "4px",
                      width: `${Math.round((phase.total / max) * 100)}%`,
                      bgcolor: PHASE_COLORS[phase.key] ?? HOME_COLORS.primary,
                    }}
                  />
                </Box>
              </ButtonBase>
              {open && (
                <Box
                  display="flex"
                  flexDirection="column"
                  gap={0.5}
                  sx={{ pl: 3.25, pb: 1.75 }}
                >
                  {phase.statuses.map((status) => (
                    <ButtonBase
                      key={status.key}
                      onClick={() => onOpenStatus(status)}
                      sx={{
                        display: "flex",
                        justifyContent: "space-between",
                        gap: 1.5,
                        px: 1.5,
                        py: 1.1,
                        borderRadius: "4px",
                        bgcolor: HOME_COLORS.soft,
                        fontSize: 13,
                        "&:hover": { bgcolor: "#EDF0F4" },
                      }}
                    >
                      <span>{status.label}</span>
                      <b>{status.count}</b>
                    </ButtonBase>
                  ))}
                </Box>
              )}
            </Box>
          );
        })}
      </Box>
    </HomePanel>
  );
}
